/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Provides user-management HTTP access for the current organisation.
 *
 * HTTP methods migrated to ApiClient (Observable-based):
 * - `loadUserList$()`        — GET  /organizations/:id/users           (Pattern A)
 * - `loadUser$()`            — GET  /organizations/:id/users/:uid      (Pattern A)
 * - `inviteUser$()`          — POST /organizations/:id/users           (Pattern A)
 * - `updateRolesForUser$()` — PATCH /organizations/:id/users/:uid/roles (Pattern B,
 *                              Datadog logging via `tap`)
 * - `deleteRolesForUser$()` — DELETE /organizations/:id/users/:uid/roles (Pattern B,
 *                              Datadog logging via `tap`)
 * - `deleteUser$()`          — DELETE /organizations/:id/users/:uid    (Pattern A)
 * - `loadAlertList$()`       — GET  /organizations/:id/users/:uid/alerts (Pattern A)
 * - `dismissAlert$()`        — DELETE /…/alerts/:alertId               (Pattern A)
 *
 * All methods resolve the current organisation ID from `UserService.userDataChange`
 * via `switchMap` before issuing the HTTP request — no manual `firstValueFrom` needed.
 *
 * Deprecated Promise wrappers (backward-compat, delegate to firstValueFrom):
 * - All original public methods are kept and marked `@deprecated`.
 *
 * `getDataProviderList()` is a pure in-memory filter — no HTTP involvement, unchanged.
 */

import {inject, Injectable, signal} from '@angular/core';
import {datadogLogs} from '@datadog/browser-logs';
import {
  firstValueFrom,
  Observable,
  switchMap,
  take,
  tap,
  map,
  filter,
  throwError,
} from 'rxjs';

import {environment} from '../environments/environment';
import {ApiClient} from './api-client.service';
import {UserService} from './user.service';
import {User} from '../models/User';
import {Role} from '../models/user-management/Role';
import {GlobalAlert} from '../models/GlobalAlert';

/** Base URL of the user-management service. */
const USER_HOST = environment.backend.user_host;

/** Builds the base URL for the users resource of an organization. */
const usersUrl = (orgId: string) => `${USER_HOST}/organizations/${orgId}/users`;

/**
 * Maps an array of role IDs to `Role` objects by looking up the label in the
 * provided available-roles list. Falls back to an empty string when no match
 * is found.
 *
 * @param roleIds        - The role IDs to map.
 * @param availableRoles - The full list of roles to look up labels from.
 */
function toRoles(roleIds: string[], availableRoles: Role[]): Role[] {
  return roleIds.map((id) => ({
    id,
    label: availableRoles.find((r) => r.id === id)?.label ?? '',
  }));
}

/** Shape of the raw user-list response envelope. */
interface UserListResponse {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly data: any[];
}

/** Shape of the raw single-user response envelope. */
interface UserResponse {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly data: any;
}

/** Shape of the raw alert-list response envelope. */
interface AlertListResponse {
  readonly data: GlobalAlert[];
}

@Injectable({
  providedIn: 'root',
})
export class UserManagementService {
  private readonly apiClient = inject(ApiClient);
  private readonly userService = inject(UserService);

  userOrganizationId = '';

  userList = signal<User[] | undefined>(undefined);
  originalUserList: User[] = [];

  // ─── Observable API (new) ────────────────────────────────────────────────────

  /**
   * Fetches and maps the user list for the current organization.
   *
   * Updates `originalUserList` and the `userList` signal after mapping.
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   */
  loadUserList$(): Observable<User[]> {
    return this.userService.orgId$.pipe(
      take(1),
      tap((orgId) => (this.userOrganizationId = orgId)),
      switchMap((orgId) =>
        this.apiClient.get<UserListResponse>(`${usersUrl(orgId)}`),
      ),
      map((response) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const resultList: User[] = response.data.map((currentUser: any) => {
          const organization = currentUser.organizations[0];
          return {
            id: currentUser.id,
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
            email: currentUser.email,
            region: currentUser.region,
            language: currentUser.language,
            picture: currentUser.picture,
            status: organization.status,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            roles: organization.roles?.map((role: any) => role.label),
            organization,
          };
        });
        return resultList.sort((a, b) =>
          a?.lastName && b?.lastName ? a.lastName.localeCompare(b.lastName) : 0,
        );
      }),
      tap((sortedList) => {
        this.originalUserList = sortedList;
        this.userList.set(this.originalUserList);
      }),
    );
  }

  /**
   * Fetches and maps a single user by ID for the current organization.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param id - The ID of the user to load.
   */
  loadUser$(id: string): Observable<User> {
    return this.userService.orgId$.pipe(
      take(1),
      tap((orgId) => (this.userOrganizationId = orgId)),
      switchMap((orgId) =>
        this.apiClient.get<UserResponse>(`${usersUrl(orgId)}/${id}`),
      ),
      map((response) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const loadedUser: any = response.data;
        const organization = loadedUser.organizations[0];
        return {
          id: loadedUser.id,
          status: organization.status,
          firstName: loadedUser.firstName,
          lastName: loadedUser.lastName,
          email: loadedUser.email,
          region: loadedUser.region,
          language: loadedUser.language,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          roles: organization.roles?.map((role: any) => role.id),
          organization,
        };
      }),
    );
  }

  /**
   * Invites a new user to the current organization.
   *
   * Maps role IDs to `{id, label}` objects before sending the request.
   * Emits `true` on success.
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param inviteUser - The invite payload (mutated in-place to enrich roles).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inviteUser$(inviteUser: any): Observable<true> {
    inviteUser.roles = toRoles(
      inviteUser.roles ?? [],
      this.userService.availableRoles,
    );

    return this.userService.orgId$.pipe(
      take(1),
      tap((orgId) => (this.userOrganizationId = orgId)),
      switchMap((orgId) =>
        this.apiClient.post<unknown>(`${usersUrl(orgId)}`, inviteUser),
      ),
      map(() => true as const),
    );
  }

  /**
   * Updates the roles of a user within the current organization.
   *
   * Logs the operation via Datadog on success and failure (Pattern B).
   * Emits `true` on success.
   *
   * @param userId - Target user ID.
   * @param roles  - Array of role IDs to assign.
   */
  updateRolesForUser$(userId: string, roles: string[]): Observable<true> {
    const updatedRoles = toRoles(roles, this.userService.availableRoles);

    return this.userService.orgId$.pipe(
      take(1),
      tap((orgId) => (this.userOrganizationId = orgId)),
      switchMap((orgId) =>
        this.apiClient.patch<void>(`${usersUrl(orgId)}/${userId}/roles`, {
          roles: updatedRoles,
        }),
      ),
      tap(() =>
        datadogLogs.logger.info(
          `updated roles (${JSON.stringify(roles)}) for ${userId}`,
        ),
      ),
      map(() => true as const),
    );
  }

  /**
   * Deletes specific roles from a user within the current organization.
   *
   * Logs the operation via Datadog on success and failure (Pattern B).
   * Emits `true` on success.
   *
   * @param userId - Target user ID.
   * @param roles  - Array of role IDs to remove.
   */
  deleteRolesForUser$(userId: string, roles: string[]): Observable<true> {
    const deletedRoles = toRoles(roles, this.userService.availableRoles);

    return this.userService.orgId$.pipe(
      take(1),
      tap((orgId) => (this.userOrganizationId = orgId)),
      switchMap((orgId) =>
        this.apiClient.delete<void>(`${usersUrl(orgId)}/${userId}/roles`, {
          roles: deletedRoles,
        }),
      ),
      tap(() =>
        datadogLogs.logger.info(
          `deleted roles (${JSON.stringify(roles)}) for ${userId}`,
        ),
      ),
      map(() => true as const),
    );
  }

  /**
   * Deletes a user from the current organization.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param userId - The ID of the user to delete.
   */
  deleteUser$(userId: string): Observable<void> {
    return this.userService.orgId$.pipe(
      take(1),
      tap((orgId) => (this.userOrganizationId = orgId)),
      switchMap((orgId) =>
        this.apiClient.delete<void>(`${usersUrl(orgId)}/${userId}`),
      ),
      map(() => undefined as void),
    );
  }

  /**
   * Fetches the alert list for the current user in the current organization.
   *
   * Throws a typed `Error` when no user is available (Pattern B).
   * Errors propagate to `GlobalErrorHandlerService` automatically otherwise (Pattern A).
   */
  loadAlertList$(): Observable<GlobalAlert[]> {
    return this.userService.userDataChange.pipe(
      take(1),
      switchMap((user) => {
        if (!user) {
          return throwError(() => new Error('User not found'));
        }
        this.userOrganizationId = user.organization.id;
        return this.apiClient.get<AlertListResponse>(
          `${usersUrl(this.userOrganizationId)}/${user.id}/alerts`,
        );
      }),
      map((response) => response.data),
    );
  }

  /**
   * Dismisses (deletes) an alert for the current user in the current organization.
   *
   * Throws a typed `Error` when no user is available (Pattern B).
   *
   * @param alertId - The ID of the alert to dismiss.
   */
  dismissAlert$(alertId: string): Observable<void> {
    return this.userService.userDataChange.pipe(
      take(1),
      switchMap((user) => {
        if (!user) {
          return throwError(() => new Error('User not found'));
        }
        this.userOrganizationId = user.organization.id;
        return this.apiClient.delete<void>(
          `${usersUrl(this.userOrganizationId)}/${user.id}/alerts/${alertId}`,
        );
      }),
      map(() => undefined as void),
    );
  }

  // ─── Pure in-memory helper (no HTTP) ─────────────────────────────────────────

  /**
   * Filters the in-memory `originalUserList` for users with the DATA_PROVIDER role.
   *
   * Not migrated — no HTTP call involved.
   */
  async getDataProviderList(): Promise<User[]> {
    return new Promise((resolve) => {
      this.userService.availableRolesChange
        .pipe(
          filter((roles): roles is NonNullable<typeof roles> => roles !== null),
          take(1),
        )
        .subscribe((roles) => {
          const dataProviderRole = roles.find(
            (role) => role.label === 'DATA_PROVIDER',
          );
          const dataProvider = this.originalUserList.filter(
            (user) =>
              user.id !== '' &&
              user.status === 'ACTIVE' &&
              user.organization.roles?.find(
                (role) => dataProviderRole?.label === role.label,
              ),
          );
          resolve(dataProvider);
        });
    });
  }

  // ─── Deprecated Promise wrappers (backward-compat) ───────────────────────────

  /** @deprecated Use `inviteUser$()` instead. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async inviteUser(inviteUser: any): Promise<boolean> {
    return firstValueFrom(this.inviteUser$(inviteUser));
  }

  /** @deprecated Use `loadUserList$()` instead. */
  async loadUserList(): Promise<User[]> {
    return firstValueFrom(this.loadUserList$());
  }

  /** @deprecated Use `loadUser$()` instead. */
  async loadUser(id: string): Promise<User> {
    return firstValueFrom(this.loadUser$(id));
  }

  /** @deprecated Use `updateRolesForUser$()` instead. */
  async updateRolesForUser(userId: string, roles: string[]): Promise<boolean> {
    return firstValueFrom(this.updateRolesForUser$(userId, roles));
  }

  /** @deprecated Use `deleteRolesForUser$()` instead. */
  async deleteRolesForUser(userId: string, roles: string[]): Promise<boolean> {
    return firstValueFrom(this.deleteRolesForUser$(userId, roles));
  }

  /** @deprecated Use `deleteUser$()` instead. */
  async deleteUser(userId: string): Promise<void> {
    return firstValueFrom(this.deleteUser$(userId));
  }

  /** @deprecated Use `loadAlertList$()` instead. */
  async loadAlertList(): Promise<GlobalAlert[]> {
    return firstValueFrom(this.loadAlertList$());
  }

  /** @deprecated Use `dismissAlert$()` instead. */
  async dismissAlert(alertId: string): Promise<void> {
    return firstValueFrom(this.dismissAlert$(alertId));
  }
}
